import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, withCtx as _withCtx, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "flex justify-between items-center" }
const _hoisted_2 = { class: "option__title" }
const _hoisted_3 = {
  key: 0,
  class: "option__desc"
}
const _hoisted_4 = { class: "option__small text-sm text-gray-600" }

import { computed, watch, onMounted, PropType, ref } from "vue";
import VueMultiselect from "vue-multiselect";
import {
  findMedicationWithGtin,
  searchForMedicationsByString,
  findMedicationWithPharmacode,
} from "@/helpers/requestHelpers/medicationRequests";
import { FormKitFrameworkContext } from "@formkit/core";
import { Medication } from "@/interfaces/medication";
import { useManagedPatientsStore } from "@/store/managedPatientsStore";
import IconButton from "@/components/IconButton.vue";
import BarcodeScanner from "@/modules/Orders/components/BarcodeScanner.vue";
import { nincdToLongString } from "@/helpers/medicationhelper";
import GtinString from "@/components/GtinString.vue";
import PublicPriceString from "@/components/PublicPriceString.vue";
import StorageQuantityString from "@/components/StorageQuantityString.vue";
import PharmacodeString from "@/components/PharmacodeString.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'MedicationSelect',
  props: {
  context: {
    type: Object as PropType<
      FormKitFrameworkContext & {
        patient?: number;
        disableCustom?: boolean;
        shopCareProviderGroupId?: number | null;
      }
    >,
    required: true,
  },
},
  setup(__props) {

const managedPatientsStore = useManagedPatientsStore();
const multiselect = ref<any>();
const props = __props;

watch(
  () => props.context.shopCareProviderGroupId,
  (prev, next) => {
    if (prev != next) {
      selectOptions.value = [];
      selectModel.value = [];
    }
  }
);

let selectOptions = ref<Medication[]>([]);
const isLoading = ref<boolean>(false);
const isTaggable = ref<boolean>(false);
const showScanner = ref<boolean>(false);
let searchStrings: Array<string> = []; // this takes care of the order of the searchstring so older search results that return after newer don't replace the more current results.

const selectModel = computed({
  get() {
    return props.context._value;
  },
  set(option) {
    if (props.context.node) {
      props.context.node.input(option ?? 0);
    }
  },
});

const noResult = computed(() => {
  if (selectModel.value && selectModel.value.length < 4) {
    return "Weiter tippen...";
  } else {
    return "Kein Medikament gefunden";
  }
});

onMounted(() => {
  asyncFind("");
});

watch(
  () => props.context.patient,
  (prev, next) => {
    if (prev != next) {
      asyncFind("");
    }
  }
);

async function asyncFind(query: string) {
  if (query.length > 3) {
    isLoading.value = true;
    isTaggable.value = false;
    if (query.length == 13 && !isNaN(parseInt(query))) {
      let res = await findMedicationWithGtin(
        parseInt(query),
        props.context.shopCareProviderGroupId
      );
      if (res.data !== null && res.error === null) {
        selectOptions.value = [res.data];
        selectModel.value = res.data;
        multiselect.value.deactivate();
      } else {
        selectOptions.value = [];
      }
    } else if (query.length != 13 && /^\d+$/.test(query)) {
      isTaggable.value = false;
      let res = await findMedicationWithPharmacode(
        parseInt(query),
        props.context.shopCareProviderGroupId
      );
      if (res.data != null && res.error === null) {
        selectOptions.value = [res.data];
      } else {
        selectOptions.value = [];
      }
    } else {
      // Probably Text-Search
      searchStrings.push(query);
      let res = await searchForMedicationsByString(
        query,
        props.context.shopCareProviderGroupId
      );
      if (searchStrings.includes(query)) {
        searchStrings = searchStrings.slice(searchStrings.indexOf(query) + 1);
        if (res.data !== null && res.error === null) {
          selectOptions.value = res.data.medications;
        } else {
          selectOptions.value = [];
        }
      }
      isTaggable.value = true;
      if (query.slice(-1) == "*") {
        isTaggable.value = false;
      }
    }
    isLoading.value = false;
  } else {
    isLoading.value = false;
    isTaggable.value = false;
    selectOptions.value = [];

    if (props.context.patient) {
      let managedPatient = managedPatientsStore.patients?.find(
        (p) => p.id == props.context.patient
      );
      if (
        managedPatient &&
        managedPatient.currentMedication &&
        managedPatient.currentMedication.length > 0
      ) {
        selectOptions.value = managedPatient.currentMedication.filter((cm) => {
          if (!cm.supplierId) {
            return true;
          } else {
            if (!props.context.shopCareProviderGroupId) {
              return true;
            } else {
              return (
                cm.isInIndex &&
                cm.supplierId == props.context.shopCareProviderGroupId
              );
            }
          }
        });
      }
    }
  }
}

function limitText(count: number) {
  return `und ${count} weitere`;
}

function addTag(newTag: string) {
  const tag = {
    dscrd: newTag,
    gtin: 0,
  };
  selectOptions.value.push(tag);
  selectModel.value = tag;
}
function scanResult(medication: Medication | null) {
  showScanner.value = false;
  if (medication == null) return;
  selectModel.value = medication;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!showScanner.value)
      ? (_openBlock(), _createBlock(_unref(VueMultiselect), {
          key: 0,
          options: _unref(selectOptions),
          modelValue: selectModel.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((selectModel).value = $event)),
          "track-by": "gtin",
          label: "dscrd",
          "allow-empty": false,
          value: selectModel.value,
          class: "w-full rounded border-2 bg-white border-darken",
          loading: isLoading.value,
          placeholder: "Suche...",
          "max-height": 150,
          "options-limit": 50,
          "limit-text": limitText,
          "internal-search": false,
          "open-direction": "bottom",
          onSearchChange: asyncFind,
          ref_key: "multiselect",
          ref: multiselect,
          "preserve-search": true,
          "show-no-options": false,
          "tag-placeholder": "Unbekanntes Medikament hinzufügen",
          taggable: !props.context.disableCustom && isTaggable.value,
          onTag: addTag
        }, {
          noResult: _withCtx(() => [
            _createTextVNode(_toDisplayString(noResult.value), 1)
          ]),
          option: _withCtx((props) => [
            (props.option.dscrd)
              ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                  _createElementVNode("span", _hoisted_2, _toDisplayString(props.option.dscrd), 1),
                  (props.option.gtin !== 0)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                        _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(nincdToLongString)(props.option.nincd)), 1),
                        _createVNode(PharmacodeString, {
                          pharmacode: props.option.pharmacode,
                          long: true,
                          class: "option__small"
                        }, null, 8, ["pharmacode"]),
                        _createVNode(GtinString, {
                          gtin: props.option.gtin,
                          long: true,
                          class: "option__small"
                        }, null, 8, ["gtin"]),
                        _createVNode(PublicPriceString, {
                          "public-price": props.option.public_price,
                          long: true,
                          class: "option__small"
                        }, null, 8, ["public-price"]),
                        _createVNode(StorageQuantityString, {
                          "storage-quantity": props.option.storage_quantity,
                          long: true,
                          class: "option__small"
                        }, null, 8, ["storage-quantity"])
                      ]))
                    : _createCommentVNode("", true)
                ], 64))
              : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                  _createTextVNode(_toDisplayString(props.option.label), 1)
                ], 64))
          ]),
          _: 1
        }, 8, ["options", "modelValue", "value", "loading", "taggable"]))
      : _createCommentVNode("", true),
    (showScanner.value)
      ? (_openBlock(), _createBlock(BarcodeScanner, {
          key: 1,
          "care-provider-group-id": props.context.shopCareProviderGroupId,
          class: "flex-1",
          onBarcodeScanned: scanResult
        }, null, 8, ["care-provider-group-id"]))
      : _createCommentVNode("", true),
    _createVNode(IconButton, {
      icon: "scan-barcode",
      class: "block m:hidden",
      onClick: _cache[1] || (_cache[1] = ($event: any) => (showScanner.value = !showScanner.value))
    })
  ]))
}
}

})